import React, { useEffect } from 'react'
import { getDeliveryQuote, saveAddress } from 'apis/vehicleApis'
import { SlideOutDropdown } from 'components/SlideOut/SlideOutDropdown'
import { useState } from 'react'
import { useBiddingSidePanel } from 'store/useBiddingSidePanelStore'
import { useVehicleStore } from 'store/useVehicleStore'
import { IDropdownData, IOption } from 'types/baseTypes'
import { DeliveryInformation, IAddress, IDeliveryQuote } from 'types/vehicleTypes'
import { shallow } from 'zustand/shallow'
import readyAutoImage from 'images/ReadyLogistics.png'
import runBuggyImage from 'images/RunBuggy.png'
import { AddressModal } from './AddressModel'
import { DeliveryChargeCard } from './VDP/DeliveryChargeCard'
import { StyledLoader, StyledModal } from './StyledModel'
import { READY_LOGISTIC_TRANSPORT_ID, READY_TOOLTIP, RUN_BUGGY_TOOLTIP, RUN_BUGGY_TRANSPORT_ID } from 'common/constants'
import { useFormikContext } from 'formik'
import { Loader } from 'components/Loader'
import { useDtmAnalytics } from 'hooks/useDtmAnalytics'
import { AnalyticsStorageKey } from 'utils/analyticsUtils'

interface IProps {
  isAddressModalOpen: boolean
  onOpenAddressModal: () => void
  onCloseAddressModal: () => void
  setSelectedDeliveryPrice: (price: number | null) => void
  selectedDeliveryAddress: IOption<IDropdownData> | null
  setSelectedDeliveryAddress: (address: IOption<IDropdownData> | null) => void
  deliveryInformation: DeliveryInformation | null
  setOtherChildId?: (id: number | null) => void
  setDeliveryInformation?: (deliveryInfo: DeliveryInformation) => void
  readyAutoPrice: number | null
  runBuggyPrice: number | null
  setReadyAutoPrice: (price: number | null) => void
  setRunBuggyPrice: (price: number | null) => void
  readyAutoError: string | null
  runBuggyError: string | null
  setReadyAutoError: (error: string | null) => void
  setRunBuggyError: (error: string | null) => void
  currentDeliveryQuote: any
  setCurrentDeliveryQuote: (quote: any) => void
  deliveryAddress: IOption<IDropdownData>[] | null
  setDeliveryAddress: (address: IOption<IDropdownData>[]) => void
}
export const TransportationPanel = React.memo(function TransportationPanel(props: IProps) {
  const {
    isAddressModalOpen,
    onOpenAddressModal,
    onCloseAddressModal,
    setSelectedDeliveryPrice,
    selectedDeliveryAddress,
    setSelectedDeliveryAddress,
    setOtherChildId,
    readyAutoPrice,
    runBuggyPrice,
    setReadyAutoPrice,
    setRunBuggyPrice,
    readyAutoError,
    runBuggyError,
    setReadyAutoError,
    setRunBuggyError,
    deliveryInformation,
    currentDeliveryQuote,
    setCurrentDeliveryQuote,
    deliveryAddress,
    setDeliveryAddress
  } = props
  const [biddingVehicleInstanceId] = useBiddingSidePanel((state) => [state.biddingVehicleInstanceId], shallow)
  const biddingVehicle = useVehicleStore((state) =>
    state.vehicles?.find((vehicleData) => vehicleData.vehicle.InstanceID === biddingVehicleInstanceId)
  )
  const [isLocationActionsVisible, setIsLocationActionsVisible] = useState(true)
  const [isSaveAddressToBuyer, setIsSaveAddressToBuyer] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedTransportProviderId, setSelectedTransportProviderId] = useState<number | null>(null)
  const formik = useFormikContext<{ BiddingDealerID: number }>()
  const { userInteraction } = useDtmAnalytics()

  useEffect(() => {
    if (deliveryInformation?.VehicleDeliveryQuotationsInfors) {
      const selectedQuote = deliveryInformation.VehicleDeliveryQuotationsInfors.find(
        (quote) => quote.IsApplyForTransport === true
      )
      if (selectedQuote) {
        setSelectedTransportProviderId(selectedQuote.TransportProviderID)
      }
    }

    // Only update deliveryAddress if it hasn't been initialized yet or if there are new addresses
    if (!deliveryAddress || deliveryAddress.length === 0) {
      setDeliveryAddress(
        deliveryInformation?.DeliveryAddresses?.filter((address) => address.ID !== 0).map((address) => ({
          item: address,
          label: address.Description,
          value: address.ID
        }))
      )
    } else {
      // Merge addresses: use deliveryAddress (updated list) as base and add any missing from deliveryInformation
      const originalAddressIds = new Set(deliveryInformation?.DeliveryAddresses?.map((addr) => addr.ID) || [])
      const newAddresses = deliveryAddress.filter((addr) => !originalAddressIds.has(addr.value))

      // Combine original addresses from deliveryInformation with newly added addresses
      const originalAddresses =
        deliveryInformation?.DeliveryAddresses?.filter((address) => address.ID !== 0).map((address) => ({
          item: address,
          label: address.Description,
          value: address.ID
        })) || []

      setDeliveryAddress([...originalAddresses, ...newAddresses])
    }
  }, [deliveryInformation])

  const handleOpenAddressModal = () => {
    userInteraction(`${window.webData.pageName} : Add New Location Clicked`, [
      {
        VIN: biddingVehicle.vehicle.VIN || 'Unknown'
      }
    ])
    onOpenAddressModal()
    setIsLocationActionsVisible(false)
  }

  const handleDeliveryAddressChange = async (selectedOption: IOption<IDropdownData> | number) => {
    let addressId: number
    let addressOption: IOption<IDropdownData> | null = null

    if (typeof selectedOption === 'number') {
      addressId = selectedOption
      addressOption = deliveryAddress.find((opt) => opt.value === addressId) || null
    } else {
      addressId = selectedOption.value
      addressOption = selectedOption
    }
    setSelectedDeliveryAddress(addressOption)

    setReadyAutoPrice(null)
    setRunBuggyPrice(null)
    setReadyAutoError(null)
    setRunBuggyError(null)
    setSelectedTransportProviderId(null)
    setSelectedDeliveryPrice(null)

    const quoteRequest: IDeliveryQuote = {
      ChannelID: biddingVehicle?.vehicle.SaleChannelID,
      InterestID: biddingVehicle?.vehicle.OfferID,
      InterestStatusID: biddingVehicle?.vehicle.OfferStatusID,
      LocationID: deliveryInformation.LocationID,
      PaymentTypeID: 0,
      SalePrice: biddingVehicle?.auctionInfo.NextBidAmount || biddingVehicle?.vehicle.FinalPrice,
      SalesSessionStepId: biddingVehicle?.vehicle.SalesSessionStepID,
      VehicleInstanceID: biddingVehicle?.vehicle.InstanceID,
      AddressID: addressId,
      PickUpAddressId: deliveryInformation.PickupAddressId,
      PickUpZIP: deliveryInformation.PickUpZIP,
      DropOffAddressId: addressId,
      DropOffZIP: null //need to update
    }

    try {
      const biddingDealerId = formik.values.BiddingDealerID || biddingVehicle.auctionInfo.BuyerID
      setIsLoading(true)

      const deliveryQuote = await getDeliveryQuote(quoteRequest, biddingDealerId)

      setCurrentDeliveryQuote(deliveryQuote)

      if (deliveryQuote?.DeliveryErrors && deliveryQuote.DeliveryErrors.length > 0) {
        const readyAutoError = deliveryQuote.DeliveryErrors.find(
          (error) => error.TransportCompanyID === READY_LOGISTIC_TRANSPORT_ID
        )
        const runBuggyError = deliveryQuote.DeliveryErrors.find(
          (error) => error.TransportCompanyID === RUN_BUGGY_TRANSPORT_ID
        )

        if (readyAutoError) {
          setReadyAutoError(readyAutoError.Message)
        }

        if (runBuggyError) {
          setRunBuggyError(runBuggyError.Message)
        }
      }
      const readyAuto = deliveryQuote?.VehicleDeliveryQuotationsInfors?.find(
        (info) => info.TransportProviderID === READY_LOGISTIC_TRANSPORT_ID
      )
      setReadyAutoPrice(readyAuto?.NonDiscountedPrice || null)

      const runBuggy = deliveryQuote?.VehicleDeliveryQuotationsInfors?.find(
        (info) => info.TransportProviderID === RUN_BUGGY_TRANSPORT_ID
      )
      setRunBuggyPrice(runBuggy?.NonDiscountedPrice || null)
    } catch (error) {
      console.error('Error fetching delivery quote:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveAddress = async (address: IAddress) => {
    try {
      onCloseAddressModal()
      setIsLoading(true)

      const currentBuyerId = formik.values.BiddingDealerID
      const result = await saveAddress({
        Address: address,
        BuyerId: currentBuyerId,
        SaveToBuyer: isSaveAddressToBuyer
      })

      if (result > 0) {
        const newAddress = {
          ID: result,
          Description: `${address.AddressLineOne}${address.AddressLineTwo ? `, ${address.AddressLineTwo}` : ''}, ${
            address.Town
          }, ${deliveryInformation?.States?.find((state) => state.ID === address.StateId)?.Code || ''} ${
            address.PostCode
          }`,
          FieldDescription:
            deliveryInformation?.States?.find((state) => state.ID === address.StateId)?.FieldDescription || ''
        }

        const newOption = {
          item: newAddress,
          label: newAddress.Description,
          value: newAddress.ID
        }

        setDeliveryAddress([...deliveryAddress, newOption])
        await handleDeliveryAddressChange(newOption)
        formik.setFieldValue('DeliveryAddressID', newOption.value)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLocationActionsVisible(true)
    }
  }

  const handleDeliveryOptionChange = (price: number | null, transportProviderId?: number) => {
    if (price !== null) {
      const transportProviderName =
        deliveryInformation?.TransportProviders.find((provider) => provider.ID === transportProviderId)?.Description ||
        'Unknown Transport Provider'

      const trackingName = `${window.webData.pageName} : Delivery Option Changed to ${transportProviderName}`
      sessionStorage.removeItem(AnalyticsStorageKey.CUSTOM_CLICK_NAME)

      if (window.s && typeof window.s.tl === 'function') {
        window.s.linkTrackVars = 'eVar20,prop20'
        window.s.linkTrackEvents = 'None'
        window.s.eVar20 = trackingName
        window.s.prop20 = trackingName
        window.s.tl(true, 'o', trackingName)
      } else {
        userInteraction(trackingName, [
          {
            VIN: biddingVehicle.vehicle.VIN || 'Unknown'
          }
        ])
      }
      setSelectedDeliveryPrice(price)
      setOtherChildId(transportProviderId)
      setSelectedTransportProviderId(transportProviderId)

      const updatedDeliveryQuote = { ...currentDeliveryQuote }
      if (updatedDeliveryQuote.VehicleDeliveryQuotationsInfors) {
        updatedDeliveryQuote.VehicleDeliveryQuotationsInfors = updatedDeliveryQuote.VehicleDeliveryQuotationsInfors.map(
          (quote) => ({
            ...quote,
            IsApplyForTransport: quote.TransportProviderID === transportProviderId
          })
        )

        setCurrentDeliveryQuote(updatedDeliveryQuote)

        if (props.setDeliveryInformation) {
          const updatedDeliveryInfo = {
            ...deliveryInformation,
            VehicleDeliveryQuotationsInfors: updatedDeliveryQuote.VehicleDeliveryQuotationsInfors
          }
          props.setDeliveryInformation(updatedDeliveryInfo)
        }
      }
    }
  }

  return (
    <StyledModal>
      {isLoading && (
        <StyledLoader>
          <Loader />
        </StyledLoader>
      )}
      <h2>Transportation Quote</h2>

      {deliveryInformation?.TransportProviders.find((provider) => provider.ID === READY_LOGISTIC_TRANSPORT_ID) && (
        <DeliveryChargeCard
          imageSrc={readyAutoImage}
          altText="Ready Auto"
          quotePrice={readyAutoPrice}
          errorMessage={readyAutoError}
          disabled={!!readyAutoError || !selectedDeliveryAddress}
          radioName="deliveryOption"
          radioValue="deliveryCharge1"
          radioId="deliveryCharge1"
          isLoading={isLoading}
          checked={selectedTransportProviderId === READY_LOGISTIC_TRANSPORT_ID}
          toolTipText={READY_TOOLTIP}
          onChange={() => handleDeliveryOptionChange(readyAutoPrice, READY_LOGISTIC_TRANSPORT_ID)}
        />
      )}
      {deliveryInformation?.TransportProviders.find((provider) => provider.ID === RUN_BUGGY_TRANSPORT_ID) && (
        <DeliveryChargeCard
          imageSrc={runBuggyImage}
          altText="Run Buggy"
          quotePrice={runBuggyPrice}
          errorMessage={runBuggyError}
          disabled={!!runBuggyError || !selectedDeliveryAddress}
          radioName="deliveryOption"
          radioValue="deliveryCharge2"
          radioId="deliveryCharge2"
          isLoading={isLoading}
          checked={selectedTransportProviderId === RUN_BUGGY_TRANSPORT_ID}
          toolTipText={RUN_BUGGY_TOOLTIP}
          onChange={() => handleDeliveryOptionChange(runBuggyPrice, RUN_BUGGY_TRANSPORT_ID)}
        />
      )}

      <SlideOutDropdown
        required
        label="Delivery Address"
        options={deliveryAddress}
        fallbackValue={selectedDeliveryAddress?.value || deliveryAddress?.[0]}
        name="DeliveryAddressID"
        onChange={(selectedOption) => handleDeliveryAddressChange(selectedOption)}
      ></SlideOutDropdown>
      {isLocationActionsVisible && (
        <div className="location-actions">
          <span
            style={{
              cursor: 'pointer'
            }}
            onClick={handleOpenAddressModal}
          >
            Add New Location
          </span>
        </div>
      )}
      <AddressModal
        show={isAddressModalOpen}
        defaultAddressDetail={null}
        deliveryInformation={deliveryInformation}
        events={{
          onClose: () => {
            onCloseAddressModal()
            setIsLocationActionsVisible(true)
          },
          handleSaveAddress: handleSaveAddress,
          setIsSaveAddressToBuyer: setIsSaveAddressToBuyer
        }}
      />
    </StyledModal>
  )
})
